<script setup lang="ts">
import { computed } from 'vue'
import type { BookPageConfig } from '../configs/types'
import { formatViewCount } from '@/helpers'

interface Props {
  book: GetContentGetcontentdetailResponse['data']
  config: BookPageConfig
  coverUrl?: string
  introduce?: string
  paragraphs: string[]
  popupTOC: boolean
  bannerShow: boolean
  isInShelf?: boolean
  shelfLoading?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (event: 'update:popup-t-o-c', value: boolean): void
  (event: 'goto-tags', tagId: string): void
  (event: 'open-toc'): void
  (event: 'read'): void
  (event: 'back'): void
  (event: 'toggle-shelf'): void
}>()

// 创建可写的 computed 用于 v-model 双向绑定
const popupTOCModel = computed({
  get: () => props.popupTOC,
  set: value => emit('update:popup-t-o-c', value),
})
</script>

<template>
  <div class="bg-n-bg-global min-h-screen text-n-text-1">
    <!-- Talewave 封面区域 - 基于 Ficmoon 布局，但使用 Talewave 配色 -->
    <div
      class="cover pt-44px pb-64px relative"
      :class="{ 'mt-60px': bannerShow }"
    >
      <!-- 背景模糊封面 -->
      <div
        class="w-full h-full absolute top-0 left-0 !bg-cover !bg-no-repeat"
        :style="`background: url(${coverUrl})`"
      >
        <!-- 返回按钮 -->
        <div
          class="absolute top-12px left-12px text-24px z-99"
          @click="emit('back')"
        >
          <i-icon-park-outline-left class="text-24px text-n-text-inverse" />
        </div>

        <!-- 遮罩层：黑色半透明覆盖，配合背后模糊 -->
        <div class="h-full" :style="{ backgroundColor: 'rgba(0,0,0,0.6)' }" />

        <!-- 模糊效果 (固定 5px 模糊) -->
        <div
          class="w-full h-full absolute left-0 top-0"
          style="backdrop-filter: blur(5px)"
        />
      </div>

      <!-- 封面内容 -->
      <div class="text-center z-1 relative top-5">
        <img
          class="w-135px h-180px mx-a rounded-8px shadow-lg"
          :src="coverUrl"
          alt="Book Cover"
        >
        <p class="mt-5 text-18px font-bold text-n-text-inverse" style="padding: 0 30px;">
          {{ book.contentName }}
        </p>
        <p class="mt-5px text-13px text-[#FFFFFF99]">
          {{ book.authorInfo?.author }}
        </p>
        <!-- 阅读数与书架数 -->
        <div class="mt-8px flex items-center justify-center text-14px text-[#FFFFFF] gap-12px">
          <div class="flex items-center">
            <img src="/images/talewave/tw_ic_eye.png" class="mr-6px" alt="views" style="width:18px;height:18px;object-fit:contain;display:inline-block;">
            <span class="text-white">{{ formatViewCount(book.viewUsers) }}</span>
          </div>
          <span class="inline-block" style="width:1px; height:18px; background-color: rgba(255,255,255,0.3);" />
          <div class="flex items-center">
            <img src="/images/talewave/tw_ic_book.png" class="mr-6px" alt="bookshelf" style="width:18px;height:18px;object-fit:contain;display:inline-block;">
            <span class="text-white">{{ book.bookshelfCount || 0 }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 简介区域 -->
    <div class="-mt-20px pt-5 px-4 pb-20px rounded-t-15px bg-white relative z-1">
      <!-- 标题 -->
      <h2 class="text-20px font-bold text-n-text-1 mb-15px">
        {{ config.texts.bookIntroduce }}
      </h2>

      <!-- 简介文本 -->
      <BasicExpandableText
        :text="book.introduce"
        :line-clamp="3"
        more-bg-color="#F6F7F9"
        text-class="text-14px lh-22px text-[#6A6E7A]"
      />

      <!-- 标签 -->
      <div class="mt-15px flex items-center flex-nowrap overflow-x-auto gap-10px text-13px">
        <div
          v-for="tag in book.tagList || []"
          :key="tag.id"
          class="flex-shrink-0 whitespace-nowrap px-10px py-5px rounded-15px cursor-pointer bg-n-bg-common-tag text-n-text-common-tag h-30px"
          @click="emit('goto-tags', tag.id || '')"
        >
          {{ tag.name }}
        </div>
      </div>
    </div>

    <!-- 章节区域 -->
    <div class="px-5 pb-5 bg-white">
      <!-- 上方分割线：使用深色小透明度以在浅色背景上可见 -->
      <div class="h-1px mb-3" style="background-color: rgba(0,0,0,0.06)" />

      <div class="flex items-center justify-between">
        <div
          class="flex items-center text-16px text-n-text-1 cursor-pointer"
          @click="emit('open-toc')"
        >
          <span>{{ book.lastChapter?.sequenceNo || 0 }} {{ config.texts.chapters }}</span>
          <i-icon-park-outline-right class="w-4 h-4 ml-5px" />
        </div>
        <div class="text-14px text-n-text-3">
          {{ book.finishStatus ? config.texts.completed : config.texts.ongoing }}
        </div>
      </div>

      <div class="my-3" style="height:10px; width:100vw; position:relative; left:50%; transform:translateX(-50%); background-color: #F6F7F9;" />

      <div class="lh-30px">
        <div v-if="introduce" class="mb-20px text-14px text-[#6A6E7A]">
          <p v-html="introduce" />
        </div>

        <h3 class="text-16px font-bold text-n-text-1 mb-15px">
          {{ book.firstChapter?.chapterName }}
        </h3>
        <p v-for="(item, index) in paragraphs.slice(0, 16)" :key="index" class="mt-4 text-14px text-n-text-2">
          {{ item }}
        </p>
      </div>
    </div>

    <!-- TOC 弹窗 -->
    <BizPopupBookTOC
      v-if="book && book.firstChapterContentUrl"
      v-model="popupTOCModel"
      :book="book"
      :content-id="book.id"
      :chapter-id="book.firstChapter?.id"
      :lock-icon-color="config.theme.tocLockColor"
      :style="{ '--toc-border-color': '#F6F7F9' }"
    />

    <!-- 底部按钮 -->
    <BasicFixedBottom class="shadow-[0px_-4px_20px_0px_rgba(0,0,0,0.15)]">
      <div>
        <div class="w-full px-5 pt-3 pb-0 bg-white" @click="emit('read')">
          <a
            class="block w-full h-45px lh-45px text-center text-15px font-bold rounded-22.5px cursor-pointer btn-solid text-n-text-inverse"
          >
            {{ !book.readFlag ? config.texts.startReading : config.texts.continueReading }}
          </a>
        </div>
      </div>
    </BasicFixedBottom>
  </div>
</template>

<style scoped>
.cover {
  background-size: cover !important;
}
</style>
